import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Chip,
  Paper,
  Box,
  TablePagination,
  TextField,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  Divider,
  TableContainer,
  InputAdornment,
  useTheme,
  alpha,
  Grid
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useThemeMode } from "../contexts/ThemeContext";
import apiService from "../services/api";

import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import ScheduleIcon from "@mui/icons-material/Schedule";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import DateRangeIcon from "@mui/icons-material/DateRange";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

interface JobRow {
  ppp_sf_jobid: string;
  ppp_sourcefilename: string;
  ppp_transactioncount: number;
  ppp_status: number;
  createdon: string;
}

const STATUS_EXTRACTED = 100000002;
const STATUS_FAILED = 100000003;
const STATUS_PROCESSING = 100000001;

const JobHistory: React.FC = () => {
  const theme = useTheme();
  const { mode } = useThemeMode();
  const navigate = useNavigate();

  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [deletingJobId, setDeletingJobId] = useState<string | null>(null);

  const loadJobs = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiService.getJobs();
      setJobs(Array.isArray(data) ? data : []);
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load history";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
  }, []);

  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      const file = (job.ppp_sourcefilename || "").toLowerCase();
      const matchesSearch = !search || file.includes(search.toLowerCase());

      const created = new Date(job.createdon);
      const matchesStart = !startDate || created >= new Date(startDate);
      const matchesEnd = !endDate || created <= new Date(endDate + "T23:59:59");

      return matchesSearch && matchesStart && matchesEnd;
    });
  }, [jobs, search, startDate, endDate]);

  const pagedJobs = useMemo(() => {
    return filteredJobs.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredJobs, page, rowsPerPage]);

  const statusChip = (status: number) => {
    if (status === STATUS_EXTRACTED)
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Extracted"
          size="small"
          sx={{
            bgcolor: alpha(theme.palette.success.main, 0.1),
            color: theme.palette.success.main,
            fontWeight: 600
          }}
        />
      );

    if (status === STATUS_FAILED)
      return (
        <Chip
          icon={<ErrorIcon />}
          label="Failed"
          size="small"
          sx={{
            bgcolor: alpha(theme.palette.error.main, 0.1),
            color: theme.palette.error.main,
            fontWeight: 600
          }}
        />
      );

    return (
      <Chip
        icon={<ScheduleIcon />}
        label="Processing"
        size="small"
        sx={{
          bgcolor: alpha(theme.palette.warning.main, 0.1),
          color: theme.palette.warning.main,
          fontWeight: 600
        }}
      />
    );
  };

  const handleDelete = async (jobId: string) => {
    try {
      setDeletingJobId(jobId);
      const ok = await apiService.deleteJob(jobId);
      if (ok) {
        setSnackbarMessage("Statement deleted successfully");
        setSnackbarOpen(true);
        await loadJobs();
      }
    } catch {
      setError("Delete failed");
    } finally {
      setDeletingJobId(null);
    }
  };

  const handleDownloadCsv = async (jobId: string) => {
    try {
      const { blob, fileName } = await apiService.exportToCsv(jobId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError("Failed to download CSV");
    }
  };

  //const getStatusIcon = (status: number) => {
   // if (status === STATUS_EXTRACTED) return <CheckCircleIcon sx={{ color: theme.palette.success.main }} />;
   // if (status === STATUS_FAILED) return <ErrorIcon sx={{ color: theme.palette.error.main }} />;
   // return <ScheduleIcon sx={{ color: theme.palette.warning.main }} />;
 // };

  if (loading) {
    return (
      <Container sx={{ mt: 6, textAlign: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2, color: mode === 'light' ? '#0A1E3C' : '#E5E5E5' }}>
          Loading history...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          fontWeight={800}
          sx={{
            background: "linear-gradient(135deg, #0A1E3C 0%, #1E3A5F 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: 1
          }}
        >
          Statement History
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View and manage all your processed bank statements
        </Typography>
      </Box>

      {/* Stats Summary */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper
            sx={{
              p: 2,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              bgcolor: alpha(theme.palette.primary.main, 0.02)
            }}
          >
            <Typography variant="caption" color="text.secondary">Total Statements</Typography>
            <Typography variant="h5" fontWeight={800}>{jobs.length}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper
            sx={{
              p: 2,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
              bgcolor: alpha(theme.palette.success.main, 0.02)
            }}
          >
            <Typography variant="caption" color="text.secondary">Extracted</Typography>
            <Typography variant="h5" fontWeight={800} color="success.main">
              {jobs.filter(j => j.ppp_status === STATUS_EXTRACTED).length}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper
            sx={{
              p: 2,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
              bgcolor: alpha(theme.palette.warning.main, 0.02)
            }}
          >
            <Typography variant="caption" color="text.secondary">Processing</Typography>
            <Typography variant="h5" fontWeight={800} color="warning.main">
              {jobs.filter(j => j.ppp_status === STATUS_PROCESSING).length}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper
            sx={{
              p: 2,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
              bgcolor: alpha(theme.palette.error.main, 0.02)
            }}
          >
            <Typography variant="caption" color="text.secondary">Failed</Typography>
            <Typography variant="h5" fontWeight={800} color="error.main">
              {jobs.filter(j => j.ppp_status === STATUS_FAILED).length}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Paper
        sx={{
          p: 3,
          borderRadius: 3,
          border: `1px solid ${mode === 'light' ? 'rgba(10, 30, 60, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
          boxShadow: mode === 'light' ? '0 4px 20px rgba(0, 0, 0, 0.02)' : '0 4px 20px rgba(0, 0, 0, 0.4)'
        }}
      >
        {/* Filters and Actions */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField
              placeholder="Search file name..."
              value={search}
              size="small"
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: mode === 'light' ? '#0A1E3C' : '#E5E5E5' }} />
                  </InputAdornment>
                )
              }}
              sx={{ minWidth: 250 }}
            />

            <TextField
              type="date"
              size="small"
              label="From"
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DateRangeIcon sx={{ color: mode === 'light' ? '#0A1E3C' : '#E5E5E5' }} />
                  </InputAdornment>
                )
              }}
            />

            <TextField
              type="date"
              size="small"
              label="To"
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DateRangeIcon sx={{ color: mode === 'light' ? '#0A1E3C' : '#E5E5E5' }} />
                  </InputAdornment>
                )
              }}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              startIcon={<RefreshIcon />}
              variant="outlined"
              onClick={loadJobs}
              sx={{
                borderColor: mode === 'light' ? '#0A1E3C' : alpha(theme.palette.secondary.main, 0.5),
                color: mode === 'light' ? '#0A1E3C' : '#E5E5E5',
                '&:hover': {
                  borderColor: theme.palette.secondary.main,
                  bgcolor: alpha(theme.palette.secondary.main, 0.04)
                }
              }}
            >
              Refresh
            </Button>

            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={() => navigate("/")}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                color: 'white',
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
                }
              }}
            >
              Upload Statement
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: mode === 'light' ? 'rgba(10, 30, 60, 0.02)' : 'rgba(255, 255, 255, 0.02)' }}>
                <TableCell><strong>Statement</strong></TableCell>
                <TableCell><strong>Transactions</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedJobs.map((job) => {
                const deleting = deletingJobId === job.ppp_sf_jobid;

                return (
                  <TableRow
                    key={job.ppp_sf_jobid}
                    hover
                    sx={{
                      '&:hover': {
                        bgcolor: alpha(theme.palette.secondary.main, 0.02)
                      }
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DescriptionIcon sx={{ color: theme.palette.secondary.main }} />
                        <Box>
                          <Typography fontWeight={600}>
                            {job.ppp_sourcefilename}
                          </Typography>
                          <Typography variant="caption" sx={{ color: mode === 'light' ? '#666' : '#AAA' }}>
                            {new Date(job.createdon).toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={job.ppp_transactioncount ?? 0}
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          fontWeight: 600
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      {statusChip(job.ppp_status)}
                    </TableCell>

                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        <Tooltip title="View Transactions">
                          <IconButton
                            disabled={deleting}
                            onClick={() => navigate(`/transactions/${job.ppp_sf_jobid}`)}
                            sx={{
                              color: theme.palette.primary.main,
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.1)
                              }
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Download CSV">
                          <IconButton
                            disabled={deleting}
                            onClick={() => handleDownloadCsv(job.ppp_sf_jobid)}
                            sx={{
                              color: theme.palette.secondary.main,
                              '&:hover': {
                                bgcolor: alpha(theme.palette.secondary.main, 0.1)
                              }
                            }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete">
                          <IconButton
                            disabled={deleting}
                            onClick={() => handleDelete(job.ppp_sf_jobid)}
                            sx={{
                              color: theme.palette.error.main,
                              '&:hover': {
                                bgcolor: alpha(theme.palette.error.main, 0.1)
                              }
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredJobs.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value));
            setPage(0);
          }}
          sx={{
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
              color: mode === 'light' ? '#666' : '#AAA'
            }
          }}
        />
      </Paper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2500}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  );
};

export default JobHistory;